<script setup>
// Used only for creating new cards from the kanban board.
// The edit flow is now inline (see PipelineCardHero, CardInfoPanel).
import { ref, watchEffect, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PipelineSelector from './PipelineSelector.vue';
import { emitter } from 'shared/helpers/mitt';
import conversationAPI from '../../../../api/inbox/conversation';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  pipelineId: {
    type: [String, Number],
    required: true,
  },
  stage: {
    type: String,
    required: true,
  },
  position: {
    type: Number,
    default: 0,
  },
  initialData: {
    type: Object,
    default: null,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  currentConversation: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['saved', 'close']);

const { t } = useI18n();
const store = useStore();

const loading = ref(false);
const loadingAgents = ref(false);
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);

// Computed para obter as etapas do funil selecionado
const availableStages = computed(() => {
  if (!selectedPipeline.value?.stages) return [];

  return Object.entries(selectedPipeline.value.stages)
    .map(([id, stage]) => ({
      id,
      name: stage.name,
      position: stage.position,
    }))
    .sort((a, b) => a.position - b.position);
});

// Refs para controle da conversa
const showConversationInput = ref(
  !!props.initialData?.item_details?.conversation_id
);
const conversations = ref([]);
const loadingConversations = ref(false);

// Ref para controlar a visibilidade do input de valor
const showValueInput = ref(!!props.initialData?.item_details?.value);

// Adicione as opções de moeda
const currencyOptions = [
  { symbol: 'R$', code: 'BRL', locale: 'pt-BR' },
  { symbol: '$', code: 'USD', locale: 'en-US' },
  { symbol: '€', code: 'EUR', locale: 'de-DE' },
];

// Helper para extrair data e hora de uma string ISO
const extractDateAndTime = isoString => {
  if (!isoString) return { date: '', time: '' };
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return { date: '', time: '' };
    const dateStr = date.toISOString().split('T')[0];
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const timeStr =
      hours !== '00' || minutes !== '00' ? `${hours}:${minutes}` : '';
    return { date: dateStr, time: timeStr };
  } catch {
    return { date: '', time: '' };
  }
};

// Extrair data/hora do initialData
const initialDeadline = extractDateAndTime(
  props.initialData?.item_details?.deadline_at ||
    props.initialData?.item_details?.deadline
);

// Refs para controle de agendamento
const showScheduleInput = ref(!!initialDeadline.date);
const scheduleDate = ref(initialDeadline.date);
const scheduleTime = ref(initialDeadline.time || '09:00');

// Fase 2.2 — Previsão de fechamento (data em que o vendedor espera fechar)
// Normaliza pra YYYY-MM-DD se vier como ISO datetime do backend.
const forecastCloseDate = ref(
  (() => {
    const raw = props.initialData?.forecast_close_date;
    if (!raw) return '';
    // Se for ISO completo (YYYY-MM-DDTHH:MM:SS...), pegar só a parte da data
    return String(raw).slice(0, 10);
  })()
);

// Opções rápidas de hora
const quickTimeOptions = [
  '08:00',
  '09:00',
  '10:00',
  '14:00',
  '15:00',
  '16:00',
  '18:00',
];

// Atualizar o form.value para incluir as ofertas e datas
const form = ref({
  title: props.initialData?.title || '',
  description:
    props.initialData?.item_details?.description ||
    props.initialData?.description ||
    '',
  pipeline_id: props.pipelineId,
  pipeline_stage: props.stage,
  position: props.position,
  item_details: {
    title: props.initialData?.title || '',
    description:
      props.initialData?.item_details?.description ||
      props.initialData?.description ||
      '',
    value: null,
    currency: currencyOptions[0],
    offers: [],
    priority:
      props.initialData?.item_details?.priority ||
      props.initialData?.priority ||
      'none',
    agent_id:
      props.initialData?.item_details?.agent_id ||
      props.initialData?.owner_id ||
      null,
    conversation_id: props.initialData?.item_details?.conversation_id || null,
  },
  custom_attributes: props.initialData?.custom_attributes || {},
});

// Ref para nova oferta (Fase 3.5 adiciona `url` opcional)
const newOffer = ref({
  description: '',
  value: '',
  currency: currencyOptions[0],
  url: '',
});

// Refs para controle das ofertas
const offers = ref([]);

// Função para buscar conversas
const fetchConversations = async () => {
  try {
    loadingConversations.value = true;
    if (props.currentConversation) {
      conversations.value = [props.currentConversation];
    } else {
      const response = await conversationAPI.get({});
      conversations.value = response.data.data.payload;
    }
  } catch (error) {
    // Error handled silently
  } finally {
    loadingConversations.value = false;
  }
};

// Modifique o onMounted para remover a busca de agentes
onMounted(async () => {
  loadingAgents.value = true;
  try {
    await store.dispatch('agents/get');
  } catch (error) {
    // Error handled silently
  } finally {
    loadingAgents.value = false;
  }

  if (
    props.currentConversation ||
    (props.isEditing && props.initialData?.item_details?.conversation_id)
  ) {
    showConversationInput.value = true;
    fetchConversations();
  }

  if (props.initialData?.item_details?.offers?.length) {
    showValueInput.value = true;
    offers.value = [...props.initialData.item_details.offers];
  } else if (props.isEditing && props.initialData?.item_details?.value) {
    // Initialize from value if no offers array exists
    showValueInput.value = true;
    offers.value = [
      {
        description: '',
        value: props.initialData.item_details.value,
        currency: props.initialData.item_details.currency || currencyOptions[0],
      },
    ];
  }
});

const rawValue = ref('');

// Atualize a função de formatação para usar a moeda selecionada
// eslint-disable-next-line no-unused-vars
const formatCurrencyInput = (e, index) => {
  let value = e.target.value.replace(/[^\d.,]/g, '');
  rawValue.value = value;
};

// Atualize também a função de validação
const validateCurrencyInput = (e, index) => {
  const target = index === 'new' ? newOffer.value : offers.value[index];
  if (!rawValue.value) {
    e.target.value = '';
    target.value = null;
    return;
  }

  const calcValue = rawValue.value.replace(',', '.');
  const number = parseFloat(calcValue);

  if (Number.isNaN(number)) {
    e.target.value = '';
    target.value = null;
    return;
  }

  e.target.value = new Intl.NumberFormat(target.currency.locale, {
    style: 'currency',
    currency: target.currency.code,
  }).format(number);

  target.value = number;
};

watch(showValueInput, newValue => {
  if (!newValue) {
    form.value.item_details.value = null;
    rawValue.value = '';
  }
});

watch(showConversationInput, newValue => {
  if (!newValue) {
    form.value.item_details.conversation_id = null;
  } else {
    fetchConversations();
  }
});

// Watch para limpar agendamento quando desmarcado
watch(showScheduleInput, newValue => {
  if (!newValue) {
    scheduleDate.value = '';
    scheduleTime.value = '09:00';
  }
});

// Atualiza o form quando o funil ou etapa mudar
watchEffect(() => {
  form.value = {
    ...form.value,
    pipeline_id: selectedPipeline.value?.id || props.pipelineId,
    pipeline_stage: form.value.pipeline_stage || props.stage,
    position: props.position,
    title:
      form.value.title ||
      props.initialData?.title ||
      props.initialData?.item_details?.title ||
      '',
    item_details: {
      ...form.value.item_details,
      title:
        form.value.title ||
        props.initialData?.title ||
        props.initialData?.item_details?.title ||
        '',
      description: form.value.description,
      priority:
        form.value.item_details?.priority ||
        props.initialData?.item_details?.priority ||
        props.initialData?.priority ||
        'none',
    },
  };
});

// Adicionar watch para sincronizar o título em tempo real
watch(
  () => form.value.title,
  newTitle => {
    if (form.value.item_details) {
      form.value.item_details.title = newTitle;
    }
  }
);

// Adicionar watch para sincronizar a descrição em tempo real
watch(
  () => form.value.description,
  newDescription => {
    if (form.value.item_details) {
      form.value.item_details.description = newDescription;
    }
  }
);

// Modificar a função validateForm
const validateForm = () => {
  const errors = {};

  if (!form.value.title?.trim()) {
    errors.title = t('PIPELINE.ERRORS.TITLE_REQUIRED');
  }

  if (!form.value.pipeline_id) {
    errors.pipeline_id = t('PIPELINE.ERRORS.PIPELINE_REQUIRED');
  }

  if (!form.value.pipeline_stage) {
    errors.pipeline_stage = t('PIPELINE.ERRORS.STAGE_REQUIRED');
  }

  if (Object.keys(errors).length > 0) {
    emitter.emit('newToastMessage', {
      message: errors[Object.keys(errors)[0]],
      type: 'error',
    });
    return errors;
  }

  return null;
};

const priorityOptions = [
  { id: 'none', name: t('PIPELINE.PRIORITY_LABELS.NONE') },
  { id: 'low', name: t('PIPELINE.PRIORITY_LABELS.LOW') },
  { id: 'medium', name: t('PIPELINE.PRIORITY_LABELS.MEDIUM') },
  { id: 'high', name: t('PIPELINE.PRIORITY_LABELS.HIGH') },
  { id: 'urgent', name: t('PIPELINE.PRIORITY_LABELS.URGENT') },
];

// Modificar handleSubmit
const handleSubmit = async e => {
  e.preventDefault();
  const errors = validateForm();
  if (errors) return;

  try {
    loading.value = true;

    form.value.item_details.title = form.value.title;
    form.value.item_details.description = form.value.description;

    let finalItemDetails = {};

    if (props.isEditing && props.initialData?.item_details) {
      finalItemDetails = JSON.parse(
        JSON.stringify(props.initialData.item_details)
      );

      const fieldsToPreserve = [
        'notes',
        'checklist',
        'attachments',
        'activities',
        'history',
      ];
      fieldsToPreserve.forEach(field => {
        if (props.initialData.item_details[field]) {
          finalItemDetails[field] = props.initialData.item_details[field];
        }
      });
    }

    const fieldsToUpdate = [
      'title',
      'description',
      'value',
      'currency',
      'priority',
      'agent_id',
      'conversation_id',
    ];

    fieldsToUpdate.forEach(field => {
      if (field in form.value.item_details) {
        finalItemDetails[field] = form.value.item_details[field];
      }
    });

    finalItemDetails.title = form.value.title;

    // Auto-add pending value if user typed a value but didn't click "+"
    if (
      showValueInput.value &&
      newOffer.value.value &&
      !Number.isNaN(Number(String(newOffer.value.value).replace(',', '.')))
    ) {
      const pendingValue = parseFloat(
        String(newOffer.value.value).replace(',', '.')
      );
      if (pendingValue > 0) {
        offers.value.push({
          description: newOffer.value.description || '',
          value: pendingValue,
          currency: newOffer.value.currency,
        });
      }
    }

    finalItemDetails.offers = offers.value;

    // Adicionar deadline_at se agendamento estiver ativo
    if (showScheduleInput.value && scheduleDate.value) {
      // Parse the date string (YYYY-MM-DD) into local timezone components
      // to avoid UTC conversion issues where selecting day 12 could become day 11
      const [year, month, day] = scheduleDate.value.split('-').map(Number);
      const [hours, minutes] = scheduleTime.value
        ? scheduleTime.value.split(':').map(Number)
        : [9, 0]; // Default 09:00
      const deadlineDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
      finalItemDetails.deadline_at = deadlineDate.toISOString();
      finalItemDetails.scheduling_type = 'deadline';
    } else {
      // Limpar deadline se desmarcado
      finalItemDetails.deadline_at = null;
      finalItemDetails.deadline = null;
      finalItemDetails.scheduling_type = null;
    }

    if (offers.value.length > 0) {
      // Sum all offer values
      finalItemDetails.value = offers.value.reduce(
        (sum, offer) => sum + (Number(offer.value) || 0),
        0
      );
      finalItemDetails.currency = offers.value[0].currency;
    }

    const payload = {
      pipeline_id: selectedPipeline.value?.id || props.pipelineId,
      pipeline_stage: form.value.pipeline_stage,
      position: props.position,
      owner_id: form.value.item_details.agent_id || null,
      expected_revenue: finalItemDetails.value || null,
      // Fase 2.2 — previsão de fechamento (date, nullable)
      forecast_close_date: forecastCloseDate.value || null,
      item_details: finalItemDetails,
    };

    if (!payload.pipeline_id) {
      emitter.emit('newToastMessage', {
        message: 'Pipeline ID é obrigatório',
        type: 'error',
      });
      loading.value = false;
      return;
    }

    const result = props.isEditing
      ? await store.dispatch('pipelineCards/updateItemDetails', {
          itemId: props.initialData.id,
          details: payload,
        })
      : await store.dispatch('pipelineCards/createPipelineCardItem', payload);

    emitter.emit('newToastMessage', {
      message: props.isEditing
        ? t('PIPELINE.MESSAGES.ITEM_UPDATED_SUCCESS')
        : t('PIPELINE.MESSAGES.ITEM_CREATED_SUCCESS'),
      type: 'success',
    });

    emit('saved', result);
    emit('close');
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_SAVE_ERROR'),
      type: 'error',
    });
  } finally {
    loading.value = false;
  }
};

// Funções para gerenciar ofertas
const addOffer = () => {
  if (newOffer.value.value) {
    const entry = {
      description: newOffer.value.description,
      value: newOffer.value.value,
      currency: newOffer.value.currency,
    };
    // Fase 3.5 — só persiste url se preenchida e valida
    const rawUrl = (newOffer.value.url || '').trim();
    if (rawUrl) entry.url = rawUrl;
    offers.value.push(entry);

    // Sum all offer values
    form.value.item_details.value = offers.value.reduce(
      (sum, offer) => sum + (Number(offer.value) || 0),
      0
    );
    form.value.item_details.currency = offers.value[0].currency;

    newOffer.value = {
      description: '',
      value: '',
      currency: currencyOptions[0],
      url: '',
    };
  }
};

const removeOffer = index => {
  offers.value.splice(index, 1);

  if (offers.value.length > 0) {
    // Sum all remaining offer values
    form.value.item_details.value = offers.value.reduce(
      (sum, offer) => sum + (Number(offer.value) || 0),
      0
    );
    form.value.item_details.currency = offers.value[0].currency;
  } else {
    form.value.item_details.value = null;
    form.value.item_details.currency = currencyOptions[0];
  }
};

// Fase 3.3 — valores rápidos pra preencher oferta com 1 click
// Lê de pipeline.settings.quick_values se configurado, senão usa default.
const DEFAULT_QUICK_VALUES = [1000, 5000, 10000, 25000, 50000];

const quickValues = computed(() => {
  const configured = selectedPipeline.value?.settings?.quick_values;
  if (Array.isArray(configured) && configured.length > 0) {
    return configured
      .map(v => Number(v))
      .filter(v => Number.isFinite(v) && v > 0);
  }
  return DEFAULT_QUICK_VALUES;
});

const applyQuickValue = value => {
  const formatted = new Intl.NumberFormat(newOffer.value.currency.locale, {
    style: 'currency',
    currency: newOffer.value.currency.code,
  }).format(value);
  newOffer.value.value = formatted;
  rawValue.value = String(value);
};

// Fase 3.2 — marca oferta como padrão para métricas.
// Click na estrela ativada desmarca (toggle). Click em outra, transfere.
const setOfferAsDefault = index => {
  const target = offers.value[index];
  if (!target) return;

  const currentlyActive = Boolean(target.is_default);
  offers.value = offers.value.map((offer, i) => ({
    ...offer,
    is_default: !currentlyActive && i === index,
  }));
};

const formatCurrencyValue = (value, currency) => {
  if (!value) return '-';

  try {
    return new Intl.NumberFormat(currency.locale, {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (error) {
    return value.toString();
  }
};
</script>

<template>
  <form
    class="flex flex-col h-full min-h-0 !p-0 !self-auto"
    @submit.prevent="handleSubmit"
  >
    <!-- Main Content - Scrollable -->
    <main class="flex-grow min-h-0 overflow-y-auto px-5 py-4 space-y-4">
      <!-- Title (full width, primary field) -->
      <div class="space-y-1.5">
        <label
          class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
        >
          {{ t('PIPELINE.FORM.TITLE.LABEL') }}
          <span class="text-n-ruby-9">*</span>
        </label>
        <input
          v-model="form.title"
          type="text"
          class="w-full bg-n-solid-2 border border-n-weak rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand transition-all placeholder-n-slate-9 text-n-slate-12"
          :placeholder="t('PIPELINE.FORM.TITLE.PLACEHOLDER')"
          required
        />
      </div>

      <!-- Value + Description row -->
      <div class="space-y-4">
        <!-- Value toggle -->
        <div class="space-y-1.5">
          <div class="flex items-center justify-between">
            <label
              class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
            >
              {{ t('PIPELINE.FORM.VALUE.LABEL') }}
            </label>
            <label class="flex items-center gap-2 cursor-pointer group">
              <input
                id="has-value"
                v-model="showValueInput"
                type="checkbox"
                class="h-3.5 w-3.5 rounded border-n-weak text-n-brand focus:ring-n-brand bg-n-solid-2 transition-colors"
              />
              <span
                class="text-xs text-n-slate-10 group-hover:text-n-slate-12 transition-colors"
              >
                {{ t('PIPELINE.FORM.VALUE.HAS_VALUE') }}
              </span>
            </label>
          </div>
          <div v-if="showValueInput" class="flex items-center gap-2">
            <div class="relative flex-1">
              <input
                v-model="newOffer.value"
                type="text"
                inputmode="decimal"
                class="w-full bg-n-solid-2 border border-n-weak rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand transition-all pr-12"
                :placeholder="t('PIPELINE.FORM.VALUE.PLACEHOLDER')"
                @input="e => formatCurrencyInput(e, 'new')"
                @blur="e => validateCurrencyInput(e, 'new')"
              />
              <span
                class="absolute right-3 top-1/2 -translate-y-1/2 text-n-slate-9 font-medium text-xs"
              >
                {{ newOffer.currency.symbol }}
              </span>
            </div>
            <button
              type="button"
              class="p-2.5 bg-n-brand hover:brightness-110 text-white rounded-lg transition-colors"
              @click="addOffer"
            >
              <fluent-icon icon="add" size="16" />
            </button>
          </div>
          <!-- Fase 3.3 — quick value presets (agilidade pra valores comuns) -->
          <div
            v-if="showValueInput && quickValues.length > 0"
            class="flex flex-wrap gap-1.5 mt-1"
          >
            <button
              v-for="preset in quickValues"
              :key="preset"
              type="button"
              class="px-2 py-0.5 text-xs rounded-full bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 border border-n-weak transition-colors"
              :title="t('PIPELINE.FORM.VALUE.QUICK_VALUE_TOOLTIP')"
              @click="() => applyQuickValue(preset)"
            >
              {{ formatCurrencyValue(preset, newOffer.currency) }}
            </button>
          </div>
          <!-- Fase 3.5 — descrição + URL opcional da oferta -->
          <div v-if="showValueInput" class="grid grid-cols-2 gap-2 mt-2">
            <input
              v-model="newOffer.description"
              type="text"
              class="bg-n-solid-2 border border-n-weak rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand transition-all"
              :placeholder="
                t('PIPELINE.FORM.VALUE.OFFER_DESCRIPTION_PLACEHOLDER')
              "
            />
            <input
              v-model="newOffer.url"
              type="url"
              class="bg-n-solid-2 border border-n-weak rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand transition-all"
              :placeholder="t('PIPELINE.FORM.VALUE.OFFER_URL_PLACEHOLDER')"
            />
          </div>
          <div
            v-if="!showValueInput"
            class="bg-n-alpha-1 border border-n-weak rounded-lg px-3 py-2 text-sm text-n-slate-9 italic"
          >
            {{ t('PIPELINE.FORM.VALUE.NO_VALUE') }}
          </div>
        </div>

        <!-- Offers list -->
        <div
          v-if="offers.length > 0"
          class="flex flex-wrap gap-2 p-3 bg-n-solid-2 rounded-lg border border-n-weak border-dashed"
        >
          <div
            v-for="(offer, index) in offers"
            :key="index"
            class="inline-flex items-center gap-1 bg-n-alpha-1 text-n-brand border border-n-weak rounded-full pl-2 pr-2 py-1 text-sm"
            :class="{ 'border-n-amber-9 bg-n-amber-3': offer.is_default }"
          >
            <button
              type="button"
              :title="
                offer.is_default
                  ? t('PIPELINE.FORM.VALUE.DEFAULT_OFFER_ACTIVE')
                  : t('PIPELINE.FORM.VALUE.DEFAULT_OFFER_SET')
              "
              class="p-0.5 rounded-full transition-colors"
              :class="
                offer.is_default
                  ? 'text-n-amber-11 hover:text-n-amber-12'
                  : 'text-n-slate-10 hover:text-n-amber-11'
              "
              @click="() => setOfferAsDefault(index)"
            >
              <fluent-icon
                :icon="offer.is_default ? 'star-filled' : 'star'"
                size="14"
              />
            </button>
            <span class="font-medium">{{
              formatCurrencyValue(offer.value, offer.currency)
            }}</span>
            <a
              v-if="offer.url"
              :href="offer.url"
              :title="offer.url"
              target="_blank"
              rel="noopener noreferrer"
              class="p-0.5 hover:bg-n-alpha-2 rounded-full text-n-brand hover:text-n-brand-11 transition-colors"
              @click.stop
            >
              <fluent-icon icon="link" size="12" />
            </a>
            <button
              type="button"
              class="p-0.5 hover:bg-n-alpha-2 rounded-full text-n-brand hover:text-n-ruby-11 transition-colors"
              @click="() => removeOffer(index)"
            >
              <fluent-icon icon="dismiss" size="12" />
            </button>
          </div>
        </div>

        <!-- Description -->
        <div class="space-y-1.5">
          <label
            class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
          >
            {{ t('PIPELINE.FORM.DESCRIPTION.LABEL') }}
          </label>
          <textarea
            v-model="form.description"
            rows="2"
            class="w-full bg-n-solid-2 border border-n-weak rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand transition-all placeholder-n-slate-9 text-n-slate-12 resize-none"
            :placeholder="t('PIPELINE.FORM.DESCRIPTION.PLACEHOLDER')"
          />
        </div>
      </div>

      <!-- Divider -->
      <div class="border-t border-n-weak" />

      <!-- Pipeline & Stage (merged row) -->
      <div class="grid grid-cols-2 gap-3">
        <div class="space-y-1.5">
          <label
            class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
          >
            {{ t('PIPELINE.FORM.PIPELINE.LABEL') }}
          </label>
          <PipelineSelector class="w-full" />
        </div>
        <div class="space-y-1.5">
          <label
            class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
          >
            {{ t('PIPELINE.FORM.STAGE.LABEL') }}
          </label>
          <select
            v-model="form.pipeline_stage"
            class="w-full bg-n-solid-2 border border-n-weak rounded-lg pl-3 py-2 text-sm focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand transition-all text-n-slate-12"
            :disabled="!selectedPipeline?.id"
          >
            <option value="" disabled>
              {{ t('PIPELINE.FORM.STAGE.PLACEHOLDER') }}
            </option>
            <option
              v-for="stage in availableStages"
              :key="stage.id"
              :value="stage.id"
            >
              {{ stage.name }}
            </option>
          </select>
        </div>
      </div>

      <!-- Priority & Agent (merged row) -->
      <div class="space-y-3">
        <!-- Priority (full width so buttons don't wrap) -->
        <div class="space-y-1.5">
          <label
            class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
          >
            {{ t('PIPELINE.FORM.PRIORITY.LABEL') }}
          </label>
          <div class="flex gap-1.5">
            <button
              v-for="option in priorityOptions"
              :key="option.id"
              type="button"
              class="flex-1 px-2 py-1.5 text-xs font-medium rounded-lg transition-all border text-center"
              :class="[
                form.item_details.priority === option.id
                  ? 'bg-n-brand text-white border-n-brand'
                  : {
                      'bg-n-solid-2 border-n-weak text-n-slate-11 hover:bg-n-alpha-2':
                        option.id === 'none',
                      'bg-n-solid-2 border-n-weak text-n-teal-11 hover:bg-n-teal-3':
                        option.id === 'low',
                      'bg-n-solid-2 border-n-weak text-n-amber-11 hover:bg-n-amber-3':
                        option.id === 'medium',
                      'bg-n-solid-2 border-n-weak text-orange-11 hover:bg-orange-3':
                        option.id === 'high',
                      'bg-n-solid-2 border-n-weak text-n-ruby-11 hover:bg-n-ruby-3':
                        option.id === 'urgent',
                    },
              ]"
              @click="form.item_details.priority = option.id"
            >
              {{ option.name }}
            </button>
          </div>
        </div>

        <!-- Agent -->
        <div class="space-y-1.5">
          <label
            class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
          >
            {{ t('PIPELINE.FORM.AGENT.LABEL') }}
          </label>
          <select
            v-model="form.item_details.agent_id"
            class="w-full bg-n-solid-2 border border-n-weak rounded-lg pl-3 py-2 text-sm focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand transition-all text-n-slate-12"
            :disabled="loadingAgents"
          >
            <option value="">
              {{ t('PIPELINE.FORM.AGENT.PLACEHOLDER') }}
            </option>
            <option
              v-for="agent in store.getters['agents/getAgents']"
              :key="agent.id"
              :value="agent.id"
            >
              {{ agent.name }}
            </option>
          </select>
        </div>
      </div>

      <!-- Divider -->
      <div class="border-t border-n-weak" />

      <!-- Schedule toggle -->
      <div class="space-y-3">
        <label class="flex items-center gap-2.5 cursor-pointer group">
          <input
            id="has-schedule"
            v-model="showScheduleInput"
            type="checkbox"
            class="h-4 w-4 rounded border-n-weak text-n-brand focus:ring-n-brand transition-colors"
          />
          <div class="flex items-center gap-2">
            <fluent-icon icon="calendar" size="16" class="text-n-slate-10" />
            <span
              class="text-sm font-medium text-n-slate-12 group-hover:text-n-brand transition-colors"
            >
              {{ t('PIPELINE.FORM.SCHEDULE.HAS_SCHEDULE') }}
            </span>
          </div>
        </label>

        <div v-if="showScheduleInput" class="pl-7 space-y-3">
          <div class="grid grid-cols-2 gap-3">
            <div class="space-y-1.5">
              <label
                class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
              >
                {{ t('PIPELINE.FORM.SCHEDULE.DATE_LABEL') }}
              </label>
              <input
                v-model="scheduleDate"
                type="date"
                class="w-full bg-n-solid-2 border border-n-weak rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand transition-all text-n-slate-12"
              />
            </div>
            <div class="space-y-1.5">
              <label
                class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
              >
                {{ t('PIPELINE.FORM.SCHEDULE.TIME_LABEL') }}
              </label>
              <input
                v-model="scheduleTime"
                type="time"
                class="w-full bg-n-solid-2 border border-n-weak rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand transition-all text-n-slate-12"
              />
            </div>
          </div>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="time in quickTimeOptions"
              :key="time"
              type="button"
              class="px-2.5 py-1 text-xs font-medium rounded-md transition-all border"
              :class="
                scheduleTime === time
                  ? 'bg-n-brand text-white border-n-brand'
                  : 'bg-n-solid-2 border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
              "
              @click="scheduleTime = time"
            >
              {{ time }}
            </button>
          </div>
        </div>
      </div>

      <!-- Fase 2.2 — Previsão de fechamento do deal -->
      <div class="space-y-3">
        <label class="flex items-center gap-2 cursor-pointer group">
          <fluent-icon
            icon="calendar-clock"
            size="16"
            class="text-n-slate-10"
          />
          <span
            class="text-sm font-medium text-n-slate-12 group-hover:text-n-brand transition-colors"
          >
            {{
              t(
                'PIPELINE.FORM.FORECAST_CLOSE_DATE.LABEL',
                'Previsão de fechamento'
              )
            }}
          </span>
          <span class="text-xs text-n-slate-10">{{
            t('PIPELINE.FORM.OPTIONAL')
          }}</span>
        </label>
        <div class="pl-6">
          <input
            v-model="forecastCloseDate"
            type="date"
            class="w-full bg-n-solid-2 border border-n-weak rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand transition-all text-n-slate-12"
          />
          <p class="text-xs text-n-slate-10 mt-1">
            {{
              t(
                'PIPELINE.FORM.FORECAST_CLOSE_DATE.HELP',
                'Quando você espera fechar este deal. Alimenta o forecast de receita no dashboard.'
              )
            }}
          </p>
        </div>
      </div>

      <!-- Conversation toggle -->
      <div class="space-y-3">
        <label class="flex items-center gap-2.5 cursor-pointer group">
          <input
            id="has-conversation"
            v-model="showConversationInput"
            type="checkbox"
            class="h-4 w-4 rounded border-n-weak text-n-brand focus:ring-n-brand transition-colors"
          />
          <div class="flex items-center gap-2">
            <fluent-icon icon="link" size="16" class="text-n-slate-10" />
            <span
              class="text-sm font-medium text-n-slate-12 group-hover:text-n-brand transition-colors"
            >
              {{ t('PIPELINE.FORM.CONVERSATION.HAS_CONVERSATION') }}
            </span>
          </div>
        </label>

        <div v-if="showConversationInput" class="pl-7">
          <select
            v-model="form.item_details.conversation_id"
            class="w-full bg-n-solid-2 border border-n-weak rounded-lg pl-3 py-2 text-sm focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand transition-all text-n-slate-12"
            :disabled="loadingConversations"
          >
            <option value="">
              {{ t('PIPELINE.FORM.CONVERSATION.PLACEHOLDER') }}
            </option>
            <option
              v-for="conversation in conversations"
              :key="conversation.id"
              :value="conversation.id"
            >
              #{{ conversation.id }} -
              {{
                conversation.meta.sender.name || conversation.meta.sender.email
              }}
            </option>
          </select>
        </div>
      </div>
    </main>

    <!-- Footer Actions (sticky bottom) -->
    <div
      class="flex items-center justify-end px-5 py-3 border-t border-n-weak bg-n-solid-1 flex-shrink-0 gap-2"
    >
      <Button
        type="button"
        variant="ghost"
        color="slate"
        size="sm"
        @click="emit('close')"
      >
        {{ t('PIPELINE.CANCEL') }}
      </Button>
      <Button
        type="submit"
        variant="solid"
        color="blue"
        size="sm"
        :is-loading="loading"
      >
        {{ t('PIPELINE.SAVE') }}
      </Button>
    </div>
  </form>
</template>
